import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Post, HydratedPost, UserData } from '../types';
import { FONT_OPTIONS } from '../constants';

interface PostVideoSharePreviewProps {
    post: Post | HydratedPost;
    authorData?: UserData;
    postBlurOpacity?: number;
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    startTime?: number;
    endTime?: number;
    mediaDuration?: number;
    onAudioStreamReady?: (stream: MediaStream) => void;
    mediaIndex?: number;
    isPreviewing?: boolean;
    setStartTime?: (val: number) => void;
    setEndTime?: (val: number) => void;
    imageBgMode?: 'borderless' | 'border';
}

const hexToRgba = (hex: string, opacity: number) => {
    try {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
        }
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return { r, g, b, a: opacity };
    } catch (e) {
        return { r: 26, g: 26, b: 46, a: opacity };
    }
};

const TimePill = ({ label, seconds, onSecondsChange, maxSeconds }: { label: string, seconds: number, onSecondsChange: (val: number) => void, maxSeconds: number }) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    const handleMinsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newMins = Math.max(0, parseInt(e.target.value) || 0);
        const newTotal = (newMins * 60) + secs;
        onSecondsChange(Math.min(maxSeconds, newTotal));
    };

    const handleSecsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSecs = Math.max(0, parseInt(e.target.value) || 0);
        const newTotal = (mins * 60) + (newSecs % 60);
        onSecondsChange(Math.min(maxSeconds, newTotal));
    };

    return (
        <div className="flex flex-col items-center gap-1.5" onClick={e => e.stopPropagation()}>
            <span className="text-[10px] text-white/50 font-black uppercase tracking-widest">{label}</span>
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl px-5 py-3 border border-white/20 flex items-center gap-2 shadow-2xl scale-110">
                <input
                    type="number"
                    value={mins}
                    onChange={handleMinsChange}
                    className="bg-transparent w-8 text-center text-white font-mono font-bold text-lg focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-white/40 font-mono font-bold text-lg">:</span>
                <input
                    type="number"
                    value={secs.toString().padStart(2, '0')}
                    onChange={handleSecsChange}
                    className="bg-transparent w-10 text-center text-white font-mono font-bold text-lg focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
            </div>
        </div>
    );
};

export const PostVideoSharePreview: React.FC<PostVideoSharePreviewProps> = ({
    post,
    authorData,
    postBlurOpacity = 80,
    canvasRef,
    startTime = 0,
    endTime = 15,
    mediaDuration = 15,
    onAudioStreamReady,
    mediaIndex,
    isPreviewing = false,
    setStartTime,
    setEndTime,
    imageBgMode = 'border'
}) => {
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    const gifImageRef = useRef<HTMLImageElement | null>(null);
    const pfpImageRef = useRef<HTMLImageElement | null>(null);
    const coverImageRef = useRef<HTMLImageElement | null>(null);
    const staticCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const [mediaAspect, setMediaAspect] = useState<number | null>(null);
    const [mediaElement, setMediaElement] = useState<HTMLMediaElement | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
    const streamDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);

    const hPost = post as HydratedPost;
    const isBump = post.type === 'bump';
    const displayPost = (isBump && hPost.originalPost && !post.content?.text && (!post.content?.media || post.content.media?.length === 0))
        ? hPost.originalPost
        : post;

    const isAudioMode = displayPost.type === 'audio' || displayPost.type === 'voice';
    const themeColor = displayPost.themeColor || '#1a1a2e';
    const rgba = useMemo(() => hexToRgba(themeColor, postBlurOpacity / 100), [themeColor, postBlurOpacity]);

    const getCorsUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('data:') || url.startsWith('blob:')) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}goodlyfe_screenshot=1`;
    };

    const displayName = authorData?.displayName || displayPost.author || 'GoodLyfe User';
    const username = displayPost.author || 'unknown';
    const pfp = getCorsUrl(authorData?.pfp || 'https://i.pravatar.cc/150?u=goodlyfe_deleted');

    const mediaAtIdx = mediaIndex !== undefined ? displayPost.content?.media?.[mediaIndex] : null;
    const firstMedia = displayPost.content?.media?.[0];
    const audioMedia = mediaAtIdx?.type === 'audio' || mediaAtIdx?.type === 'voice' ? mediaAtIdx : displayPost.content?.media?.find(m => m.type === 'audio' || m.type === 'voice');
    const visualMedia = (mediaAtIdx?.type === 'video' || mediaAtIdx?.url?.toLowerCase().endsWith('.gif')) ? mediaAtIdx : displayPost.content?.media?.find(m => m.type === 'video' || m.url?.toLowerCase().endsWith('.gif'));

    const isGif = visualMedia?.url?.toLowerCase().endsWith('.gif');
    const mediaUrl = isAudioMode
        ? getCorsUrl(audioMedia?.url || firstMedia?.url)
        : getCorsUrl(visualMedia?.url);
    const coverUrl = isAudioMode ? getCorsUrl(displayPost.content?.coverImageUrl || mediaAtIdx?.url || firstMedia?.url) : null;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;

        canvas.width = 1080;
        canvas.height = 1920;

        const staticCanvas = document.createElement('canvas');
        staticCanvas.width = 1080;
        staticCanvas.height = 1920;
        const sCtx = staticCanvas.getContext('2d');
        if (!sCtx) return;

        const wrapText = (c: CanvasRenderingContext2D, text: string, maxWidth: number) => {
            if (!text) return [];
            const words = text.split(' ');
            const lines = [];
            let currentLine = '';
            for (let i = 0; i < words.length; i++) {
                const testLine = currentLine + words[i] + ' ';
                const metrics = c.measureText(testLine);
                if (metrics.width > maxWidth && i > 0) {
                    lines.push(currentLine);
                    currentLine = words[i] + ' ';
                } else {
                    currentLine = testLine;
                }
            }
            lines.push(currentLine);
            return lines;
        };

        const layout = (() => {
            const isBorderless = imageBgMode === 'borderless';
            
            sCtx.fillStyle = isBorderless ? 'transparent' : 'black';
            if (!isBorderless) {
                sCtx.fillRect(0, 0, 1080, 1920);
                sCtx.fillStyle = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
                sCtx.fillRect(0, 0, 1080, 1920);
            }

            const fontFam = FONT_OPTIONS.find(f => f.id === displayPost.fontId)?.family || 'sans-serif';

            const cardWidth = isBorderless ? 960 : 920;
            const cardX = (1080 - cardWidth) / 2;
            const padding = isBorderless ? 0 : 60;
            const contentWidth = cardWidth - (padding * 2);
            const headerHeight = 110;

            const fontSizes = {
                xs: { size: 28, height: 38 },
                sm: { size: 36, height: 48 },
                med: { size: 46, height: 62 },
                lg: { size: 72, height: 92 }
            };
            const currentFontSize = fontSizes[displayPost.textSize as keyof typeof fontSizes] || fontSizes.med;

            sCtx.font = `500 ${currentFontSize.size}px ${fontFam}`;
            const textLines = displayPost.content?.text ? wrapText(sCtx, displayPost.content.text, contentWidth) : [];
            const textLineHeight = currentFontSize.height;
            const textHeight = textLines.length > 0 ? (textLines.length * textLineHeight) : 0;
            const textSpacing = textHeight > 0 ? (isBorderless ? 20 : 40) : 0;

            let mediaH = isAudioMode ? contentWidth : 600;
            if (mediaAspect) {
                mediaH = contentWidth / mediaAspect;
                if (mediaH > 1000) mediaH = 1000;
            }

            const totalHeight = padding + headerHeight + textSpacing + textHeight + 40 + mediaH + 40 + padding;
            
            // For borderless, we push text to the bottom
            const cardY = isBorderless 
                ? (1920 - headerHeight - textSpacing - textHeight - 80) // bottom aligned
                : ((1920 - totalHeight) / 2); // center aligned

            if (!isBorderless) {
                sCtx.save();
                sCtx.shadowBlur = 40;
                sCtx.shadowColor = 'rgba(0,0,0,0.5)';
                sCtx.fillStyle = 'rgba(0,0,0,0.4)';
                sCtx.beginPath();
                sCtx.roundRect(cardX, cardY, cardWidth, totalHeight, 48);
                sCtx.fill();
                sCtx.restore();
            }

            // Draw a subtle gradient behind text for borderless readability
            if (isBorderless) {
                const grad = sCtx.createLinearGradient(0, cardY - 200, 0, 1920);
                grad.addColorStop(0, 'rgba(0,0,0,0)');
                grad.addColorStop(1, 'rgba(0,0,0,0.85)');
                sCtx.fillStyle = grad;
                sCtx.fillRect(0, cardY - 200, 1080, 1920 - (cardY - 200));
            }

            const pfpImg = pfpImageRef.current;
            const pfpSize = isBorderless ? 80 : 100;
            if (pfpImg && pfpImg.complete && pfpImg.naturalWidth > 0) {
                sCtx.save();
                sCtx.beginPath();
                sCtx.arc(cardX + padding + (pfpSize/2), cardY + padding + (pfpSize/2), pfpSize/2, 0, Math.PI * 2);
                sCtx.clip();
                sCtx.drawImage(pfpImg, cardX + padding, cardY + padding, pfpSize, pfpSize);
                sCtx.restore();
            }

            const textX = cardX + padding + pfpSize + 24;
            
            // Helper for sticker text
            const drawStickerText = (text: string, x: number, y: number, fontStr: string, color: string, outlineColor: string) => {
                sCtx.font = fontStr;
                sCtx.lineJoin = 'round';
                sCtx.miterLimit = 2;
                
                sCtx.shadowBlur = 8;
                sCtx.shadowColor = 'rgba(0,0,0,0.6)';
                sCtx.shadowOffsetY = 4;

                sCtx.strokeStyle = outlineColor;
                sCtx.lineWidth = Math.max(4, parseInt(fontStr)/8);
                sCtx.strokeText(text, x, y);
                
                sCtx.shadowBlur = 0;
                sCtx.shadowOffsetY = 0;
                
                sCtx.fillStyle = color;
                sCtx.fillText(text, x, y);
            };

            const themeColor = displayPost.themeColor || '#3b82f6';
            const textColor = displayPost.textColor || 'white';
            const authorColor = isBorderless ? themeColor : 'white';

            sCtx.textBaseline = 'alphabetic';
            drawStickerText(displayName, textX, cardY + padding + (pfpSize*0.45), `bold ${isBorderless ? 40 : 36}px ${fontFam}`, authorColor, isBorderless ? 'white' : 'transparent');
            drawStickerText(`@${username.toLowerCase()}`, textX, cardY + padding + (pfpSize*0.85), `${isBorderless ? 32 : 28}px ${fontFam}`, 'rgba(255,255,255,0.9)', 'transparent');

            let tY = cardY + padding + headerHeight + textSpacing;
            if (textHeight > 0) {
                textLines.forEach(line => {
                    drawStickerText(line, cardX + padding, tY, `500 ${currentFontSize.size}px ${fontFam}`, textColor, isBorderless ? themeColor : 'transparent');
                    tY += textLineHeight;
                });
            }

            sCtx.fillStyle = 'rgba(255,255,255,0.6)';
            sCtx.font = `bold 26px ${fontFam}`;
            sCtx.textAlign = 'right';
            sCtx.fillText('Shared via GoodLyfe', cardX + cardWidth - padding, cardY + (isBorderless ? headerHeight + textSpacing + textHeight + 40 : totalHeight - padding + 10));
            sCtx.textAlign = 'left';

            return { cardX, cardY, padding, contentWidth, textHeight, textY: tY, mediaHeight: mediaH, currentFontSize, isBorderless };
        })();

        let timeoutId: any;
        let hasSought = false;

        const drawFrame = () => {
            if (layout.isBorderless) {
                ctx.fillStyle = 'black';
                ctx.fillRect(0, 0, 1080, 1920);
            } else {
                ctx.drawImage(staticCanvas, 0, 0);
            }

            const mX = layout.isBorderless ? 0 : (layout.cardX + layout.padding);
            const mY = layout.isBorderless ? 0 : (layout.textHeight > 0 ? layout.textY - 10 : layout.cardY + layout.padding + 110 + 10);
            const mWidth = layout.isBorderless ? 1080 : layout.contentWidth;
            const mHeight = layout.isBorderless ? 1920 : layout.mediaHeight;

            const drawMedia = (element: HTMLVideoElement | HTMLImageElement | HTMLAudioElement) => {
                ctx.save();
                ctx.beginPath();
                if (!layout.isBorderless) {
                    ctx.roundRect(mX, mY, mWidth, mHeight, 40);
                } else {
                    ctx.rect(mX, mY, mWidth, mHeight);
                }
                ctx.clip();

                if (isAudioMode) {
                    const coverImg = coverImageRef.current;
                    if (coverImg && coverImg.complete && coverImg.naturalWidth > 0) {
                        ctx.globalAlpha = 0.5;

                        // Logic for Cover (fill with crop)
                        const naturalWidth = coverImg.naturalWidth;
                        const naturalHeight = coverImg.naturalHeight;
                        const aspect = naturalWidth / naturalHeight;
                        const targetAspect = mWidth / mHeight;

                        let sW = naturalWidth;
                        let sH = naturalHeight;
                        let sX = 0;
                        let sY = 0;

                        if (aspect > targetAspect) {
                            sW = naturalHeight * targetAspect;
                            sX = (naturalWidth - sW) / 2;
                        } else {
                            sH = naturalWidth / targetAspect;
                            sY = (naturalHeight - sH) / 2;
                        }

                        ctx.drawImage(coverImg, sX, sY, sW, sH, mX, mY, mWidth, mHeight);
                        ctx.globalAlpha = 1.0;
                    } else {
                        ctx.fillStyle = 'rgba(255,255,255,0.05)';
                        ctx.fillRect(mX, mY, mWidth, mHeight);
                    }
                } else {
                    // Logic to maintain aspect ratio
                    const naturalWidth = (element as any).videoWidth || (element as any).naturalWidth || mWidth;
                    const naturalHeight = (element as any).videoHeight || (element as any).naturalHeight || mHeight;
                    const aspect = naturalWidth / naturalHeight;
                    const targetAspect = mWidth / mHeight;

                    let drawW = mWidth;
                    let drawH = mHeight;
                    let drawX = mX;
                    let drawY = mY;

                    if (layout.isBorderless) {
                        // cover
                        if (aspect > targetAspect) {
                            drawW = mHeight * aspect;
                            drawX = mX + (mWidth - drawW) / 2;
                        } else {
                            drawH = mWidth / aspect;
                            drawY = mY + (mHeight - drawH) / 2;
                        }
                    } else {
                        // contain
                        if (aspect > targetAspect) {
                            drawH = mWidth / aspect;
                            drawY = mY + (mHeight - drawH) / 2;
                        } else {
                            drawW = mHeight * aspect;
                            drawX = mX + (mWidth - drawW) / 2;
                        }
                    }

                    ctx.drawImage(element as any, drawX, drawY, drawW, drawH);
                }
                ctx.restore();

                // If borderless, we draw the static overlay ON TOP of the video
                if (layout.isBorderless) {
                    ctx.drawImage(staticCanvas, 0, 0);
                }
            };

            if (isAudioMode) {
                const audio = audioElementRef.current;
                if (audio) {
                    if (!hasSought) { try { audio.currentTime = startTime; } catch (e) { } hasSought = true; }
                    if (audio.paused) audio.play().catch(() => { });
                }
                drawMedia(audio as any);
                if (analyserRef.current) {
                    const analyser = analyserRef.current;
                    const bufferLength = analyser.frequencyBinCount;
                    const dataArray = new Uint8Array(bufferLength);
                    analyser.getByteFrequencyData(dataArray);

                    ctx.save();
                    ctx.beginPath(); ctx.roundRect(mX, mY, mWidth, mHeight, 40); ctx.clip();
                    const barWidth = (mWidth / bufferLength) * 2.5;
                    let x = mX;
                    for (let i = 0; i < bufferLength; i++) {
                        const bH = (dataArray[i] / 255) * (mHeight * 0.6);
                        ctx.fillStyle = `hsla(${(i / bufferLength) * 360}, 70%, 70%, 0.8)`;
                        ctx.fillRect(x, mY + mHeight - bH, barWidth, bH);
                        x += barWidth + 1;
                    }
                    ctx.restore();
                }
            } else if (isGif) {
                const gif = gifImageRef.current;
                if (gif && gif.complete && gif.naturalWidth > 0) {
                    drawMedia(gif);
                }
            } else {
                const video = videoElementRef.current;
                if (video && video.readyState >= 2) {
                    if (!hasSought) { try { video.currentTime = startTime; } catch (e) { } hasSought = true; }
                    if (video.paused) video.play().catch(() => { });
                    drawMedia(video);
                }
            }
            timeoutId = setTimeout(drawFrame, 1000 / 30);
        };
        drawFrame();
        return () => clearTimeout(timeoutId);
    }, [rgba, displayName, username, displayPost.content?.text, isGif, isAudioMode, coverUrl, startTime, mediaAspect]);

    useEffect(() => {
        if (!mediaElement) return;

        const initAudio = async () => {
            try {
                const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
                const audioCtx = audioContextRef.current || new AudioContextClass();
                if (audioCtx.state === 'suspended') await audioCtx.resume();
                const analyser = analyserRef.current || audioCtx.createAnalyser();
                let source = sourceRef.current;
                if (!source) {
                    try {
                        source = audioCtx.createMediaElementSource(mediaElement); sourceRef.current = source;
                    } catch (e) { console.error("Source creation failed", e); }
                }
                if (source) {
                    const streamDest = streamDestRef.current || audioCtx.createMediaStreamDestination();
                    try { source.disconnect(); } catch (e) { }

                    // Route to both analyser (visuals) and streamDest (recording)
                    source.connect(analyser);
                    source.connect(streamDest);

                    const gainNode = audioCtx.createGain();
                    gainNode.gain.value = isPreviewing ? 1 : 0.05;
                    source.connect(gainNode);
                    gainNode.connect(audioCtx.destination);
                    analyser.fftSize = 256;
                    audioContextRef.current = audioCtx; analyserRef.current = analyser; streamDestRef.current = streamDest;
                    if (onAudioStreamReady) onAudioStreamReady(streamDest.stream);
                }
                mediaElement.volume = 1; mediaElement.muted = false;
                if (mediaElement.paused) mediaElement.play().catch(() => { });
            } catch (e) { console.error("Audio Context failed", e); }
        };

        initAudio();
        mediaElement.addEventListener('play', initAudio);
        if (mediaElement.readyState >= 2 && !mediaElement.paused) initAudio();
        return () => mediaElement.removeEventListener('play', initAudio);
    }, [mediaElement, isPreviewing, onAudioStreamReady]);

    const handleUpdateStart = (val: number) => {
        if (!setStartTime || !setEndTime) return;
        setStartTime(val);
        if (endTime <= val) setEndTime(Math.min(mediaDuration, val + 1));
        if (endTime > val + 15) setEndTime(val + 15);
    };

    const handleUpdateEnd = (val: number) => {
        if (!setStartTime || !setEndTime) return;
        setEndTime(val);
        if (startTime >= val) setStartTime(Math.max(0, val - 1));
        if (startTime < val - 15) setStartTime(val - 15);
    };

    return (
        <div
            className={`${isPreviewing ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{
                position: 'fixed', left: isPreviewing ? '50%' : '-5000px', top: isPreviewing ? '50%' : '0',
                transform: isPreviewing ? 'translate(-50%, -50%) scale(0.3)' : 'none',
                width: '1080px', height: '1920px',
                opacity: isPreviewing ? 1 : 0.01, overflow: 'hidden', zIndex: isPreviewing ? 300 : -10,
                borderRadius: isPreviewing ? '40px' : '0', boxShadow: isPreviewing ? '0 0 50px rgba(0,0,0,0.5)' : 'none',
                background: 'black'
            }}
        >
            <canvas ref={canvasRef} />

            {isPreviewing && (
                <div
                    className="absolute bottom-40 left-0 right-0 p-10 flex flex-col items-center gap-10"
                    style={{ transform: 'scale(2.5)', transformOrigin: 'bottom' }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-center gap-12 bg-black/40 backdrop-blur-3xl p-8 rounded-[40px] border border-white/10 shadow-3xl">
                        <TimePill label="Start" seconds={startTime} onSecondsChange={handleUpdateStart} maxSeconds={mediaDuration} />
                        <div className="w-8 h-[2px] bg-white/20 mt-6" />
                        <TimePill label="Stop" seconds={endTime} onSecondsChange={handleUpdateEnd} maxSeconds={mediaDuration} />
                    </div>
                    <div className="bg-blue-500/20 text-blue-400 font-black px-6 py-2 rounded-full border border-blue-500/30 text-xs uppercase tracking-widest animate-pulse">
                        {(endTime - startTime).toFixed(1)}s Selected (Max 15s)
                    </div>
                </div>
            )}

            {mediaUrl && isGif && (
                <img ref={gifImageRef} src={mediaUrl} crossOrigin="anonymous" alt="h-gif"
                    onLoad={(e) => setMediaAspect(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)} />
            )}
            {mediaUrl && !isGif && !isAudioMode && (
                <video ref={(el) => { videoElementRef.current = el; setMediaElement(el); }} src={mediaUrl.startsWith('blob:') ? mediaUrl : `${mediaUrl}#t=0.001`} crossOrigin="anonymous" muted={false} playsInline autoPlay={true}
                    className="absolute opacity-0 pointer-events-none"
                    onLoadedMetadata={(e) => { setMediaAspect(e.currentTarget.videoWidth / e.currentTarget.videoHeight); e.currentTarget.volume = 1; }} />
            )}
            {mediaUrl && isAudioMode && (
                <audio ref={(el) => { audioElementRef.current = el; setMediaElement(el); }} src={mediaUrl} crossOrigin="anonymous" autoPlay={true} playsInline
                    onLoadedMetadata={(e) => { setMediaAspect(1); e.currentTarget.volume = 1; }} />
            )}
            <img ref={pfpImageRef} src={pfp} crossOrigin="anonymous" alt="h-pfp" />
            {coverUrl && (
                <img ref={coverImageRef} src={coverUrl} crossOrigin="anonymous" alt="h-cover"
                    onLoad={(e) => { if (isAudioMode) setMediaAspect(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight); }} />
            )}
        </div>
    );
};
